'use client'

import Link from 'next/link'
import {
  FaArrowUp,
  FaFire,
  FaVideo,
  FaUsers,
  FaChevronDown,
} from 'react-icons/fa'
import { useTranslation } from 'react-i18next'
import Card from './Card'
import { columnsCountBreakPoints, gutter } from '../helpers/config'

// Keep the static sample grid aligned with Explore without a client-only
// masonry mount: these portrait cards can render in reading order on the server.
const gridStyles = Object.entries(columnsCountBreakPoints)
  .sort(([first], [second]) => Number(first) - Number(second))
  .map(
    ([breakpoint, columns]) => `@media (min-width: ${breakpoint}px) {
    [data-static-explore-grid] { grid-template-columns: repeat(${columns}, minmax(0, 1fr)); }
  }`,
  )
  .join('\n')

// An opt-in landing treatment; other sample feeds retain Explore's breakpoints.
// The extra attribute keeps its rules isolated even if two feeds share a page.
const featuredGridStyles = `
  [data-static-explore-grid][data-featured-explore-grid] { grid-template-columns: minmax(0, 1fr); }
  @media (min-width: 600px) {
    [data-static-explore-grid][data-featured-explore-grid] { grid-template-columns: repeat(2, minmax(0, 1fr)); }
  }
  @media (min-width: 1024px) {
    [data-static-explore-grid][data-featured-explore-grid] { grid-template-columns: repeat(3, minmax(0, 1fr)); }
  }
`

const filters = [
  { Icon: FaArrowUp, label: 'home_filterTop', ariaLabel: 'home_ariaTopImages' },
  {
    Icon: FaFire,
    label: 'home_filterNewest',
    ariaLabel: 'home_ariaNewestImages',
  },
  { Icon: FaVideo, label: 'home_filterVideo', ariaLabel: 'home_ariaVideos' },
  {
    Icon: FaUsers,
    label: 'home_filterFollowing',
    ariaLabel: 'home_ariaFollowing',
  },
]

// Shared by the original home and the new landing: the real Explore cards,
// populated from a local sample array without mounting the live feed or queries.
export default function StaticExploreFeed({
  images,
  signupOnly = false,
  trackActions = false,
  featuredGrid = false,
  imageSizes = '(min-width: 1440px) 15vw, (min-width: 1024px) 19vw, (min-width: 768px) 25vw, 38vw',
}) {
  const { t } = useTranslation()

  return (
    <div data-static-explore-feed>
      <nav
        aria-label={t('home_ariaImageFilters')}
        className="mb-4 flex flex-wrap items-center justify-between gap-1 sm:gap-3"
      >
        <ul className="m-0 flex flex-wrap items-center gap-0 p-0 list-none sm:gap-1">
          {filters.map(({ Icon, label, ariaLabel }) => (
            <li key={label}>
              <Link
                href="/signup"
                prefetch={false}
                aria-label={t(ariaLabel)}
                data-landing-action={trackActions ? 'signup' : undefined}
                className="flex h-11 w-11 items-center justify-center gap-2 rounded-full bg-[var(--secondary-bg-color)] text-sm font-semibold text-white transition-colors hover:bg-[var(--foreground-bg-color)] md:w-auto md:px-5"
              >
                <Icon aria-hidden="true" />
                <span className="hidden md:inline">{t(label)}</span>
              </Link>
            </li>
          ))}
        </ul>
        <Link
          href="/signup"
          prefetch={false}
          aria-label={t('home_chooseStyle')}
          data-landing-action={trackActions ? 'signup' : undefined}
          className="inline-flex min-h-[44px] items-center justify-center gap-2 rounded-full bg-[var(--secondary-bg-color)] px-2 text-sm font-semibold text-white transition-colors hover:bg-[var(--foreground-bg-color)] sm:gap-3 sm:px-4"
        >
          {t('home_styleAll')}
          <FaChevronDown className="h-2.5 w-2.5" aria-hidden="true" />
        </Link>
      </nav>

      {images?.length ? (
        <section
          aria-label={t('landingV2_galleryAria')}
          data-static-explore-grid
          data-featured-explore-grid={featuredGrid ? '' : undefined}
          style={{ display: 'grid', gap: gutter }}
        >
          <style>{featuredGrid ? featuredGridStyles : gridStyles}</style>
          {images.map((image, index) => (
            <article
              key={image.id}
              className="min-w-0 overflow-hidden rounded-xl"
              data-landing-action={trackActions ? 'gallery_card' : undefined}
              data-landing-item={trackActions ? String(index + 1) : undefined}
            >
              <Card
                {...image}
                imageData={image}
                model={image.model}
                hideOverlaysMobile
                nsfw={image.nsfw}
                signupOnly={signupOnly}
                shouldLazyLoad
                imageSizes={imageSizes}
              />
            </article>
          ))}
        </section>
      ) : (
        <p className="my-12 text-center">{t('home_noImagesFound')}</p>
      )}
    </div>
  )
}
